// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Import du logo
import logo from '../assets/images/MKD_STUDIO.png';

/* ==== Drapeaux SVG inline ==== */
const FlagFR = (props) => (
  <svg viewBox="0 0 3 2" width="18" height="12" {...props}>
    <rect width="1" height="2" x="0" y="0" fill="#0055A4" />
    <rect width="1" height="2" x="1" y="0" fill="#fff" />
    <rect width="1" height="2" x="2" y="0" fill="#EF4135" />
  </svg>
);
const FlagGB = (props) => (
  <svg viewBox="0 0 60 30" width="18" height="12" {...props}>
    <rect width="60" height="30" fill="#012169"/>
    <g stroke="#fff" strokeWidth="6">
      <path d="M0,0 60,30 M60,0 0,30" />
    </g>
    <g stroke="#C8102E" strokeWidth="4" clipPath="url(#s)">
      <path d="M0,0 60,30 M60,0 0,30" />
    </g>
    <g stroke="#fff" strokeWidth="10">
      <path d="M30,0 v30 M0,15 h60" />
    </g>
    <g stroke="#C8102E" strokeWidth="6">
      <path d="M30,0 v30 M0,15 h60" />
    </g>
    <clipPath id="s">
      <path d="M30,15 h30 v15 z v-30 z h-30 z h-30 z v30 z h30 z" />
    </clipPath>
  </svg>
);
const FlagNL = (props) => (
  <svg viewBox="0 0 3 2" width="18" height="12" {...props}>
    <rect width="3" height="2" fill="#21468B"/>
    <rect width="3" height="1.333" y="0" fill="#fff"/>
    <rect width="3" height="0.666" y="0" fill="#AE1C28"/>
  </svg>
);

/* ==== Menu déroulant langues ==== */
const LanguageDropdown = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const langs = [
    { code: 'fr', label: 'Français', flag: <FlagFR /> },
    { code: 'en', label: 'English', flag: <FlagGB /> },
    { code: 'nl', label: 'Nederlands', flag: <FlagNL /> }
  ];
  const current = langs.find(l => l.code === i18n.language?.slice(0,2)) || langs[0];

  const switchLang = (lng) => {
    i18n.changeLanguage(lng);
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'transparent',
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: 6,
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          color: 'inherit',
          minWidth: '44px',
          minHeight: '44px',
          justifyContent: 'center'
        }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {current.flag} 
        <span style={{ fontSize: 12, display: window.innerWidth > 768 ? 'inline' : 'none' }}>
          {current.label}
        </span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            marginTop: 4,
            background: '#fff',
            color: '#000',
            borderRadius: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            zIndex: 999,
            minWidth: '120px'
          }}
        >
          {langs.map(lang => (
            <div
              key={lang.code}
              onClick={() => switchLang(lang.code)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                cursor: 'pointer',
                fontSize: 14,
                background: lang.code === current.code ? 'rgba(0,0,0,0.05)' : 'transparent',
                minHeight: '44px'
              }}
            >
              {lang.flag} {lang.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ==== Navbar ==== */
const Navbar = () => {
  const { t } = useTranslation('common');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const NavLinks = () => (
    <>
      <Link 
        to="/" 
        style={{ 
          color: '#fff', 
          textDecoration: 'none',
          transition: 'color 0.2s ease',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '12px 20px' : '0',
          height: isMobile ? 'auto' : '100%',
          fontSize: isMobile ? '18px' : '16px'
        }}
        onClick={() => setIsMenuOpen(false)}
        onMouseOver={(e) => e.target.style.color = '#ff3366'}
        onMouseOut={(e) => e.target.style.color = '#fff'}
      >
        {t('nav.home')}
      </Link>
      <Link 
        to="/horaires" 
        style={{ 
          color: '#fff', 
          textDecoration: 'none',
          transition: 'color 0.2s ease',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '12px 20px' : '0',
          height: isMobile ? 'auto' : '100%',
          fontSize: isMobile ? '18px' : '16px'
        }}
        onClick={() => setIsMenuOpen(false)}
        onMouseOver={(e) => e.target.style.color = '#ff3366'}
        onMouseOut={(e) => e.target.style.color = '#fff'}
      >
        {t('nav.schedule')}
      </Link>
      <Link 
        to="/formation-crew" 
        style={{ 
          color: '#fff', 
          textDecoration: 'none',
          transition: 'color 0.2s ease',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '12px 20px' : '0',
          height: isMobile ? 'auto' : '100%',
          fontSize: isMobile ? '18px' : '16px'
        }}
        onClick={() => setIsMenuOpen(false)}
        onMouseOver={(e) => e.target.style.color = '#ff3366'}
        onMouseOut={(e) => e.target.style.color = '#fff'}
      >
        {t('nav.crew')}
      </Link>
      <Link 
        to="/inscription" 
        style={{ 
          color: '#fff', 
          textDecoration: 'none',
          transition: 'color 0.2s ease',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '12px 20px' : '0',
          height: isMobile ? 'auto' : '100%',
          fontSize: isMobile ? '18px' : '16px'
        }}
        onClick={() => setIsMenuOpen(false)}
        onMouseOver={(e) => e.target.style.color = '#ff3366'}
        onMouseOut={(e) => e.target.style.color = '#fff'}
      >
        {t('nav.registration')}
      </Link>
      <Link 
        to="/blog" 
        style={{ 
          color: '#fff', 
          textDecoration: 'none',
          transition: 'color 0.2s ease',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '12px 20px' : '0',
          height: isMobile ? 'auto' : '100%',
          fontSize: isMobile ? '18px' : '16px'
        }}
        onClick={() => setIsMenuOpen(false)}
        onMouseOver={(e) => e.target.style.color = '#ff3366'}
        onMouseOut={(e) => e.target.style.color = '#fff'}
      >
        Blog
      </Link> 
      <Link 
        to="/a-propos" 
        style={{ 
          color: '#fff', 
          textDecoration: 'none',
          transition: 'color 0.2s ease',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '12px 20px' : '0',
          height: isMobile ? 'auto' : '100%',
          fontSize: isMobile ? '18px' : '16px'
        }}
        onClick={() => setIsMenuOpen(false)}
        onMouseOver={(e) => e.target.style.color = '#ff3366'}
        onMouseOut={(e) => e.target.style.color = '#fff'}
      >
        {t('nav.about')}
      </Link>
    </>
  );

  return (
    <>
      <nav style={{ 
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        padding: '0.8rem 0',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        height: isMobile ? 'auto' : '70px',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1000
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          flexDirection: isMobile ? 'row' : 'row',
          justifyContent: 'space-between',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '0 16px' : '0 20px',
          width: '100%',
          height: '100%'
        }}>
          {/* Gauche : logo */}
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            height: isMobile ? '60px' : '100%',
            zIndex: 1001
          }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <img 
                src={logo} 
                alt="MKD Studio" 
                style={{ 
                  height: isMobile ? '45px' : '55px', 
                  width: 'auto',
                  transition: 'transform 0.3s ease',
                  display: 'block'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              />
            </Link>
          </div>

          {/* Menu pour desktop */}
          {!isMobile && (
            <>
              <div style={{ flex: 1 }} />
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '24px',
                height: '100%'
              }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '28px', 
                  alignItems: 'center',
                  fontSize: '16px',
                  height: '100%'
                }}>
                  <NavLinks />
                </div>

                <LanguageDropdown />
              </div>
            </>
          )}

          {/* Menu pour mobile */}
          {isMobile && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              height: '100%'
            }}>
              <LanguageDropdown />
              
              <button 
                aria-label="Menu"
                aria-expanded={isMenuOpen}
                onClick={toggleMenu}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  display: 'flex',
                  height: '100%',
                  alignItems: 'center',
                  minWidth: '44px',
                  minHeight: '44px',
                  justifyContent: 'center',
                  padding: 0
                }}
              >
                {isMenuOpen ? '✕' : '☰'}
              </button>
            </div>
          )}
        </div>

        {/* Menu mobile déployé */}
        {isMobile && isMenuOpen && (
          <div 
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
              boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              padding: '16px 0',
              zIndex: 999,
              gap: '10px'
            }}
          >
            <NavLinks />
          </div>
        )}
      </nav>

      {/* Overlay pour menu mobile */}
      {isMobile && isMenuOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 998
          }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Navbar;